/**
 * <cart-recommendations> — related-product strip for the cart drawer.
 *
 * Fetches the `cart-recommendations` section standalone through the Product
 * Recommendations route, seeded by the first product added to the cart, and
 * injects the rendered cards.
 *
 * It fetches once and never refreshes: the host carries data-skip-subtree-update
 * so the cart's section morph (assets/morph.js) leaves the injected cards in
 * place on every cart update. Recommendations therefore stay based on the first
 * product in the cart for the life of the drawer.
 *
 * Attributes:
 *   data-url         - routes.product_recommendations_url
 *   data-section-id  - section filename to render ("cart-recommendations")
 *   data-product-id  - seed product id
 *   data-limit       - max products to request
 *   data-intent      - "related" | "complementary"
 */
class CartRecommendations extends HTMLElement {
  connectedCallback() {
    // Guard against re-fetching if the element is ever reconnected.
    if (this.dataset.loaded === 'true') return;

    const productId = this.dataset.productId;
    if (!productId) {
      this.hidden = true;
      return;
    }

    this.dataset.loaded = 'true';
    this.#fetchRecommendations(productId);
  }

  /**
   * @param {string} productId
   */
  #fetchRecommendations(productId) {
    const base = this.dataset.url;
    const sectionId = this.dataset.sectionId || 'cart-recommendations';
    const limit = this.dataset.limit || '10';
    const intent = this.dataset.intent || 'related';

    if (!base) return;

    const requestUrl =
      `${base}?section_id=${encodeURIComponent(sectionId)}` +
      `&product_id=${encodeURIComponent(productId)}` +
      `&limit=${encodeURIComponent(limit)}` +
      `&intent=${encodeURIComponent(intent)}`;

    fetch(requestUrl)
      .then((response) => (response.ok ? response.text() : ''))
      .then((text) => {
        const parsed = new DOMParser().parseFromString(text, 'text/html');
        const inner = parsed.querySelector('.cart-recommendations__inner');

        if (inner && inner.querySelector('.cart-recommendations__card')) {
          // JS only injects the Liquid-rendered markup; rendering stays in Liquid.
          this.replaceChildren(inner);
          this.hidden = false;
        } else {
          this.hidden = true;
        }
      })
      .catch((error) => {
        console.error('Error fetching cart recommendations:', error);
        this.hidden = true;
      });
  }
}

if (!customElements.get('cart-recommendations')) {
  customElements.define('cart-recommendations', CartRecommendations);
}
